import { helpWhy } from '../../help/why';
import React, { useCallback, useEffect, useState } from 'react';
import {
    Box, Button, IconButton, MenuItem, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon, Save as SaveIcon, Close as CloseIcon } from '@mui/icons-material';
import { useToast } from '../../context/ToastContext';
import { AuthService } from '../../services/auth.service';
import { StyleBomService } from '../../services/styleBom.service';
import { ItemService } from '../../services/item.service';
import { IStyleBomLine, IStyleBomLineCreateRequest, MaterialType } from '../../interfaces/garments/styleBom.interface';
import { Item } from '../../interfaces/item.interface';
import ItemAutocomplete from './ItemAutocomplete';
import { enumLabel } from '../../utils/garmentsLabels';

// Mirrors `StyleBomLineCreateDTO`'s @Size bound (backend/.../merch/dataclass/StyleBomLineCreateDTO.java).
const MAX_UOM = 30;

/** Extracts a readable message from an axios error body, mirroring `ToastContext#showApiError`'s shape handling. */
const extractApiErrorMessage = (e: any): string | undefined => {
    const data = e?.response?.data;
    if (typeof data === 'string' && data) return data;
    if (data && typeof data === 'object') {
        const candidate = [data.message, data.error].find(
            (c: unknown): c is string => typeof c === 'string' && c.length > 0);
        return candidate;
    }
    return undefined;
};

const DUPLICATE_ITEM_MESSAGE_MATCH = /already exists/i;

const MATERIAL_TYPES: MaterialType[] = ['FABRIC', 'YARN', 'TRIMS', 'ACCESSORIES', 'PACKING', 'OTHER'];

interface DraftLine {
    item: Item | null;
    materialType: MaterialType | '';
    quantityPerUnit: string;
    uom: string;
    wastagePercent: string;
    remarks: string;
}

const emptyDraft: DraftLine = {
    item: null, materialType: '', quantityPerUnit: '', uom: '', wastagePercent: '', remarks: '',
};

const draftFromLine = (line: IStyleBomLine, itemLookup: Record<string, Item>): DraftLine => ({
    item: itemLookup[line.itemId] ?? null,
    materialType: line.materialType ?? '',
    quantityPerUnit: String(line.quantityPerUnit),
    uom: line.uom,
    wastagePercent: line.wastagePercent != null ? String(line.wastagePercent) : '',
    remarks: line.remarks ?? '',
});

interface StyleBomEditorProps {
    styleId: string;
    canManage: boolean;
}

/**
 * BOM line editor hosted inside `StyleForm` (garments-merchandising T7, gap row 9.3). Unlike
 * `StyleCostSheet`'s replace-all save, each BOM line has its own create/update/delete endpoint
 * (`StyleBomService`), so rows are added/edited/deleted one at a time against the server and the
 * table reloads after every mutation.
 */
export function StyleBomEditor({ styleId, canManage }: StyleBomEditorProps) {
    const toast = useToast();

    const [lines, setLines] = useState<IStyleBomLine[]>([]);
    const [itemLookup, setItemLookup] = useState<Record<string, Item>>({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const [adding, setAdding] = useState(false);
    const [addDraft, setAddDraft] = useState<DraftLine>(emptyDraft);
    const [addItemError, setAddItemError] = useState<string | null>(null);

    const [editingId, setEditingId] = useState<string | null>(null);
    const [editDraft, setEditDraft] = useState<DraftLine>(emptyDraft);

    const loadItemLookup = useCallback(async () => {
        try {
            const res = await ItemService.getItemsByInstitution(0, 500);
            setItemLookup(Object.fromEntries((res.content || []).map(i => [i.id as string, i])));
        } catch (e) {
            // Non-fatal — the table falls back to "Item unavailable" when a lookup is missing.
        }
    }, []);

    const load = useCallback(async () => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId) return;
        try {
            setLoading(true);
            const res = await StyleBomService.list(institutionId, styleId);
            setLines(res);
        } catch (e) {
            toast.showApiError(e, 'Failed to load BOM lines');
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [styleId]);

    useEffect(() => {
        loadItemLookup();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [styleId]);

    const itemDisplay = (itemId: string) => {
        const item = itemLookup[itemId];
        return item ? `${item.itemName} (${item.itemCode})` : 'Item unavailable';
    };

    // Items already carrying a live BOM line on this style — disabled in the add-row picker so the
    // 400 "BOM line for this item already exists" round trip is avoided in the common case.
    const existingItemIds = new Set(lines.map(l => l.itemId));

    const startAdd = () => {
        setAddDraft(emptyDraft);
        setAddItemError(null);
        setAdding(true);
    };

    const cancelAdd = () => {
        setAdding(false);
        setAddDraft(emptyDraft);
        setAddItemError(null);
    };

    const addWhy = saving ? null
        : !addDraft.item ? 'Pick the item first.'
        : null;

    const handleAdd = async () => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId || !addDraft.item) return;
        const qty = parseFloat(addDraft.quantityPerUnit);
        if (Number.isNaN(qty) || qty <= 0 || !addDraft.uom.trim()) {
            toast.showError('Enter a quantity per unit greater than 0 and a UoM.');
            return;
        }
        const request: IStyleBomLineCreateRequest = {
            institutionId,
            styleId,
            itemId: addDraft.item.id as string,
            materialType: addDraft.materialType || null,
            quantityPerUnit: qty,
            uom: addDraft.uom.trim(),
            wastagePercent: addDraft.wastagePercent.trim() === '' ? null : parseFloat(addDraft.wastagePercent),
            remarks: addDraft.remarks.trim() || null,
        };
        setSaving(true);
        setAddItemError(null);
        try {
            await StyleBomService.create(institutionId, request);
            toast.showSuccess('BOM line added');
            cancelAdd();
            await load();
        } catch (e) {
            toast.showApiError(e, 'Failed to add BOM line');
            const apiMessage = extractApiErrorMessage(e);
            if (apiMessage && DUPLICATE_ITEM_MESSAGE_MATCH.test(apiMessage)) {
                setAddItemError(apiMessage);
            }
        } finally {
            setSaving(false);
        }
    };

    const startEdit = (line: IStyleBomLine) => {
        setEditingId(line.id);
        setEditDraft(draftFromLine(line, itemLookup));
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditDraft(emptyDraft);
    };

    const handleSaveEdit = async (id: string) => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId) return;
        const qty = parseFloat(editDraft.quantityPerUnit);
        if (Number.isNaN(qty) || qty <= 0 || !editDraft.uom.trim()) {
            toast.showError('Enter a quantity per unit greater than 0 and a UoM.');
            return;
        }
        setSaving(true);
        try {
            await StyleBomService.update(institutionId, id, {
                materialType: editDraft.materialType || null,
                quantityPerUnit: qty,
                uom: editDraft.uom.trim(),
                wastagePercent: editDraft.wastagePercent.trim() === '' ? null : parseFloat(editDraft.wastagePercent),
                remarks: editDraft.remarks.trim() || null,
            });
            toast.showSuccess('BOM line updated');
            cancelEdit();
            await load();
        } catch (e) {
            toast.showApiError(e, 'Failed to update BOM line');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id: string) => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId) return;
        if (!window.confirm('Delete this BOM line?')) return;
        setSaving(true);
        try {
            await StyleBomService.remove(institutionId, id);
            toast.showSuccess('BOM line deleted');
            await load();
        } catch (e) {
            toast.showApiError(e, 'Failed to delete BOM line');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="subtitle1">Bill of Materials</Typography>
                {canManage && !adding && (
                    <Button size="small" startIcon={<AddIcon />} data-help="merchandising.bom.add" onClick={startAdd} disabled={saving}>
                        Add Line
                    </Button>
                )}
            </Box>
            <TableContainer component={Paper} variant="outlined">
                <Table size="small" data-testid="bom-table">
                    <TableHead>
                        <TableRow>
                            <TableCell data-help="merchandising.bom.col.item">Item</TableCell>
                            <TableCell data-help="merchandising.bom.col.materialType">Material Type</TableCell>
                            <TableCell align="right" data-help="merchandising.bom.col.qtyPerUnit">Qty/Unit</TableCell>
                            <TableCell data-help="merchandising.bom.col.uom">UoM</TableCell>
                            <TableCell align="right" data-help="merchandising.bom.col.wastage">Wastage %</TableCell>
                            <TableCell data-help="merchandising.bom.col.remarks">Remarks</TableCell>
                            {canManage && <TableCell align="right" data-help="merchandising.bom.col.actions">Actions</TableCell>}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading && (
                            <TableRow>
                                <TableCell colSpan={canManage ? 7 : 6} align="center">Loading…</TableCell>
                            </TableRow>
                        )}
                        {!loading && lines.length === 0 && !adding && (
                            <TableRow>
                                <TableCell colSpan={canManage ? 7 : 6} align="center">No BOM lines</TableCell>
                            </TableRow>
                        )}
                        {!loading && lines.map(line => {
                            const isEditing = editingId === line.id;
                            return (
                                <TableRow key={line.id} data-testid={`bom-line-${line.id}`}>
                                    <TableCell sx={{ minWidth: 280 }}>
                                        {isEditing ? itemDisplay(line.itemId) : itemDisplay(line.itemId)}
                                    </TableCell>
                                    <TableCell sx={{ minWidth: 130 }}>
                                        {isEditing ? (
                                            <TextField
                                                data-help="merchandising.bom.col.materialType" select size="small" fullWidth value={editDraft.materialType}
                                                onChange={e => setEditDraft(prev => ({ ...prev, materialType: e.target.value as MaterialType }))}
                                            >
                                                <MenuItem value="">—</MenuItem>
                                                {MATERIAL_TYPES.map(t => <MenuItem key={t} value={t}>{enumLabel(t)}</MenuItem>)}
                                            </TextField>
                                        ) : (line.materialType ?? '—')}
                                    </TableCell>
                                    <TableCell align="right" sx={{ minWidth: 90 }}>
                                        {isEditing ? (
                                            <TextField
                                                data-help="merchandising.bom.col.qtyPerUnit" size="small" type="number" value={editDraft.quantityPerUnit}
                                                onChange={e => setEditDraft(prev => ({ ...prev, quantityPerUnit: e.target.value }))}
                                            />
                                        ) : line.quantityPerUnit}
                                    </TableCell>
                                    <TableCell sx={{ minWidth: 80 }}>
                                        {isEditing ? (
                                            <TextField
                                                data-help="merchandising.bom.col.uom" size="small" value={editDraft.uom} inputProps={{ maxLength: MAX_UOM }}
                                                onChange={e => setEditDraft(prev => ({ ...prev, uom: e.target.value }))}
                                            />
                                        ) : line.uom}
                                    </TableCell>
                                    <TableCell align="right" sx={{ minWidth: 90 }}>
                                        {isEditing ? (
                                            <TextField
                                                data-help="merchandising.bom.col.wastage" size="small" type="number" value={editDraft.wastagePercent}
                                                onChange={e => setEditDraft(prev => ({ ...prev, wastagePercent: e.target.value }))}
                                            />
                                        ) : (line.wastagePercent ?? '—')}
                                    </TableCell>
                                    <TableCell sx={{ minWidth: 150 }}>
                                        {isEditing ? (
                                            <TextField
                                                data-help="merchandising.bom.col.remarks" size="small" fullWidth value={editDraft.remarks}
                                                onChange={e => setEditDraft(prev => ({ ...prev, remarks: e.target.value }))}
                                            />
                                        ) : (line.remarks ?? '—')}
                                    </TableCell>
                                    {canManage && (
                                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                            {isEditing ? (
                                                <>
                                                    <IconButton data-help="merchandising.bom.saveEdit" size="small" onClick={() => handleSaveEdit(line.id)} disabled={saving} aria-label="save">
                                                        <SaveIcon fontSize="small" />
                                                    </IconButton>
                                                    <IconButton data-help="merchandising.bom.cancelEdit" size="small" onClick={cancelEdit} disabled={saving} aria-label="cancel edit">
                                                        <CloseIcon fontSize="small" />
                                                    </IconButton>
                                                </>
                                            ) : (
                                                <>
                                                    <IconButton data-help="merchandising.bom.edit" size="small" onClick={() => startEdit(line)} disabled={saving} aria-label="edit">
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                    <IconButton data-help="merchandising.bom.delete" size="small" onClick={() => handleDelete(line.id)} disabled={saving} aria-label="delete">
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </>
                                            )}
                                        </TableCell>
                                    )}
                                </TableRow>
                            );
                        })}
                        {adding && (
                            <TableRow data-testid="bom-add-row">
                                <TableCell sx={{ minWidth: 280 }}>
                                    <ItemAutocomplete
                                        data-help="merchandising.bom.col.item"
                                        value={addDraft.item}
                                        onChange={item => {
                                            setAddDraft(prev => ({ ...prev, item }));
                                            setAddItemError(null);
                                        }}
                                        label="" placeholder="Item"
                                        getOptionDisabled={item => existingItemIds.has(item.id as string)}
                                        error={!!addItemError}
                                        helperText={addItemError ?? undefined}
                                    />
                                </TableCell>
                                <TableCell sx={{ minWidth: 130 }}>
                                    <TextField
                                        data-help="merchandising.bom.col.materialType" select size="small" fullWidth value={addDraft.materialType}
                                        onChange={e => setAddDraft(prev => ({ ...prev, materialType: e.target.value as MaterialType }))}
                                    >
                                        <MenuItem value="">—</MenuItem>
                                        {MATERIAL_TYPES.map(t => <MenuItem key={t} value={t}>{enumLabel(t)}</MenuItem>)}
                                    </TextField>
                                </TableCell>
                                <TableCell align="right" sx={{ minWidth: 90 }}>
                                    <TextField
                                        data-help="merchandising.bom.col.qtyPerUnit" size="small" type="number" value={addDraft.quantityPerUnit}
                                        onChange={e => setAddDraft(prev => ({ ...prev, quantityPerUnit: e.target.value }))}
                                    />
                                </TableCell>
                                <TableCell sx={{ minWidth: 80 }}>
                                    <TextField
                                        data-help="merchandising.bom.col.uom" size="small" value={addDraft.uom} inputProps={{ maxLength: MAX_UOM }}
                                        onChange={e => setAddDraft(prev => ({ ...prev, uom: e.target.value }))}
                                    />
                                </TableCell>
                                <TableCell align="right" sx={{ minWidth: 90 }}>
                                    <TextField
                                        data-help="merchandising.bom.col.wastage" size="small" type="number" value={addDraft.wastagePercent}
                                        onChange={e => setAddDraft(prev => ({ ...prev, wastagePercent: e.target.value }))}
                                    />
                                </TableCell>
                                <TableCell sx={{ minWidth: 150 }}>
                                    <TextField
                                        data-help="merchandising.bom.col.remarks" size="small" fullWidth value={addDraft.remarks}
                                        onChange={e => setAddDraft(prev => ({ ...prev, remarks: e.target.value }))}
                                    />
                                </TableCell>
                                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                    <IconButton data-help="merchandising.bom.saveNew" {...helpWhy(addWhy ? { kind: 'status', message: addWhy } : null)} size="small" onClick={handleAdd} disabled={saving || !addDraft.item} aria-label="save new line">
                                        <SaveIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton data-help="merchandising.bom.cancelNew" size="small" onClick={cancelAdd} disabled={saving} aria-label="cancel new line">
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}

export default StyleBomEditor;
